import { CpuChipIcon, DocumentTextIcon, LinkIcon, ExclamationTriangleIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const RiskManagementDetails = ({ risks }) => {
    console.log({ risks });

    // If you want to show ALL risks, remove the .slice(0, 2)
    // If you want to show a limited number but more than 2, change the number
    const displayRisks = risks; // Show all risks

    // Or if you want to show first 5: risks.slice(0, 5)
    // Or if you want to paginate, add pagination logic

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Risk Details</h3>
                            <p className="text-sm text-gray-600">Showing {displayRisks.length} of {risks.length} risks</p>
                        </div>
                    </div>
                    {risks.length > displayRisks.length && (
                        <button className="text-sm text-blue-600 hover:text-blue-800">
                            View All {risks.length} Risks →
                        </button>
                    )}
                </div>

                {displayRisks.length === 0 ? (
                    <div className="text-center py-12">
                        <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No risk details available</h3>
                        <p className="text-gray-600">Add risks to see detailed information here.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {displayRisks.map((risk) => (
                            <motion.div
                                key={risk.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <h4 className="text-lg font-semibold text-gray-900">Risk: {risk.risk_id}</h4>
                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${risk.inherent_impact_type?.toLowerCase().includes('major') ? 'bg-red-100 text-red-800 border border-red-200' :
                                                risk.inherent_impact_type?.toLowerCase().includes('moderate') ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                                    risk.inherent_impact_type?.toLowerCase().includes('minor') ? 'bg-green-100 text-green-800 border border-green-200' :
                                                        'bg-gray-100 text-gray-800 border border-gray-200'
                                                }`}>
                                                {risk.inherent_impact_type || 'Not Specified'}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                                                {risk.category || 'No Category'}
                                            </span>
                                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                                                {risk.risk_sub_category || 'No Sub-category'}
                                            </span>
                                            <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-full">
                                                {risk.products_and_service || 'No Service'}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">Description:</h5>
                                    <p className="text-sm text-gray-600 line-clamp-3">{risk.risk_description}</p>
                                </div>

                                <div className="space-y-4">
                                    {/* Risk Score Information */}
                                    <div className="grid grid-cols-3 gap-2 text-center">
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <div className="text-xs text-gray-500">Impact</div>
                                            <div className="text-sm font-semibold text-gray-900">{risk.inherent_impact_type}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <div className="text-xs text-gray-500">Likelihood</div>
                                            <div className="text-sm font-semibold text-gray-900">{risk.inherent_likelihood_type}</div>
                                        </div>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <div className="text-xs text-gray-500">Risk Score</div>
                                            <div className={`text-sm font-bold ${risk.inherent_risk_score?.toLowerCase().includes('significant') ? 'text-green-600' :
                                                risk.inherent_risk_score?.toLowerCase().includes('moderate') ? 'text-yellow-600' :
                                                    risk.inherent_risk_score?.toLowerCase().includes('extreme') ? 'text-red-600' :
                                                        'text-gray-600'
                                                }`}>
                                                {risk.inherent_risk_score}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Meta Information */}
                                    <div className="flex items-center justify-between text-sm text-gray-500 border-t border-gray-100 pt-3">
                                        <div className="flex items-center space-x-4">
                                            <div>
                                                <span className="font-medium">Created:</span> {risk.created_by || 'System'}
                                            </div>
                                            <div>
                                                <span className="font-medium">Date:</span> {
                                                    risk.created_at ? new Date(risk.created_at).toLocaleDateString() : 'N/A'
                                                }
                                            </div>
                                        </div>
                                        <div className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                                            {risk.risk_status || 'Open'}
                                        </div>
                                    </div>
                                </div>

                                {/* <div className="mt-4 pt-4 border-t border-gray-200">
                                    <button className="inline-flex items-center px-3 py-2 bg-blue-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200">
                                        <CpuChipIcon className="w-4 h-4 mr-2" />
                                        AI-Generated Summary
                                    </button>
                                </div> */}
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RiskManagementDetails;